import React, { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionListItem, Avatar, Box, Button, Highlight, Icon } from '@node-space/ui-components'
import { NetworkMember } from 'types/bvnkNetwork'

interface SentRequestsHistory {
  [property: string]: boolean
}

export interface ConnectionsListProps {
  networkMembers: NetworkMember[]
  sentRequests: SentRequestsHistory
  connectToNetworkMember: (networkMember: NetworkMember) => void
  isUpdatingConnection: boolean
}

const NetworkMemberConnectionsList = ({
  networkMembers,
  sentRequests,
  connectToNetworkMember,
  isUpdatingConnection,
}: ConnectionsListProps) => {
  const { t } = useTranslation()

  const [networkMemberConnectingTo, setNetworkMemberConnectingTo] = useState<NetworkMember>()

  const actionItemListItems = useMemo(() => {
    return networkMembers?.map(networkMember => {
      const { accountReference } = networkMember?.account || {}
      return {
        testId: `network-member-connection-list-item-${accountReference}`,
        id: accountReference,
        key: accountReference,
        Avatar: <Avatar name={networkMember?.account?.name} size="small" type="entity" />,
        title: networkMember?.account?.name,
        BodyPost: networkMember?.account?.description,
        ...(sentRequests[accountReference]
          ? {
              ActionHighlight: (
                <Highlight variant="emphasis" type="success" iconElement={<Icon name="Check" />}>
                  {t('bvnkNetwork.requestSent')}
                </Highlight>
              ),
            }
          : {
              ActionButton: (
                <Button
                  iconElement={<Icon name="UserPlusCustom" />}
                  noPadding
                  variant="ghost"
                  onClick={() => {
                    setNetworkMemberConnectingTo(networkMember)
                    connectToNetworkMember(networkMember)
                  }}
                  loading={
                    isUpdatingConnection &&
                    networkMemberConnectingTo?.account?.accountReference === accountReference
                  }
                >
                  {t('bvnkNetwork.connect')}
                </Button>
              ),
            }),
      }
    })
  }, [
    networkMembers,
    isUpdatingConnection,
    sentRequests,
    networkMemberConnectingTo,
    connectToNetworkMember,
  ])

  return (
    <Box flexCol>
      {actionItemListItems?.map(item => {
        return <ActionListItem key={item.id} {...item} />
      })}
    </Box>
  )
}

export default memo(NetworkMemberConnectionsList)
